import React from 'react';
import { Checkbox, Divider, Input, Button as NUIButton, Select, Selection, SelectItem } from '@nextui-org/react';
import { useNavigate } from 'react-router';
import CareGiversTable from '../../components/tables/CareGiversTable';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../../../store/reducer-main';
import { TablePaginationConfig } from '../../../types/common';
import { CareGiver, CaregiverAssignedFamilyData, CareGiverCovergaeAvailability, CaregiverWithAssignedFamilyData } from '../../../types/caregivers';
import { deleteUserCareGiver, getCaregiversTableMaxPageNumber, initCaregiverTable, loadCaregiverNextTablePage, loadCaregiverPreviousTablePage, updateCaregiverWarning } from '../../../service/caregiovers';
import { DEFAULT_TABLE_PAGINATION } from '../../../constants/pagination';
import theme from '../../theme';
import PageLayout from '../../components/common/PageLayout';
import Typography from '../../components/common/Typography';
import Button from '../../components/common/Button';
import { CaregiverTableFilter } from '../../../types/filters';
import { getUserFamily, removeCaregiverFromFamily } from '../../../service/families';
import { toast } from 'react-toastify';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import TextButton from '../../components/common/TextButton';
import { CAREGIVER_COVERAGE_AVAILABILITY } from '../../../constants/caregivers';
import { brkRef } from '../../../helpers/utils';

export default function CareGiversPage() {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userProfile } = useSelector((state: StoreState) => state.profile);

  const [loading, setLoading] = React.useState<boolean>(false);
  // PAGINAZIONE
  const [page, setPage] = React.useState<CaregiverWithAssignedFamilyData[]>([]);
  const [paginationConfig, setPaginationConfig] = React.useState<TablePaginationConfig>(DEFAULT_TABLE_PAGINATION);
  const [totalNumeberOfPages, setTotalNumberOfPages] = React.useState<number>(1);
  // FILTERS
  const [nameFilter, setNameFilter] = React.useState<string>('');
  const [surnameFilter, setSurnameFilter] = React.useState<string>('');
  const [ageFilter, setAgeFilter] = React.useState<string>('');
  const [engagedFilter, setEngagedFilter] = React.useState<boolean>(false);
  const [notEngaedFilter, setNotEngagedFilter] = React.useState<boolean>(false);
  const [coverageFilter, setCoverageFilter] = React.useState<Selection>(new Set([]));

  React.useEffect(() => {
    init();
  }, []);

  const init = async () => {
    if (!userProfile) return;
    const page = await initCaregiverTable(userProfile.uid);
    const newCaregiverPage = await integrateCaregiverPageWithAssignedFamilyData(page.data, userProfile.uid);
    const nop = await getCaregiversTableMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size);
    setPaginationConfig({page: 1, maxPage: nop, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, secondDoc: page.cursors.second, lastDoc: page.cursors.last});
    setTotalNumberOfPages(nop);
    setPage(newCaregiverPage);
  }

  const applyTableFilters = async () => {
    if (!userProfile) return;
    const tableFilters = constructTableFilterObj();
    const page = await loadCaregiverNextTablePage(userProfile.uid, DEFAULT_TABLE_PAGINATION, tableFilters);
    const newCaregiverPage = await integrateCaregiverPageWithAssignedFamilyData(page.data, userProfile.uid);
    const maxPageNumber = await getCaregiversTableMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size, tableFilters);
    setPaginationConfig({page: 1, maxPage: maxPageNumber, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, lastDoc: page.cursors.last, secondDoc: page.cursors.second});
    setTotalNumberOfPages(maxPageNumber);
    setPage(newCaregiverPage);
  }

  const discardFilters = () => {
    setNameFilter('');
    setSurnameFilter('');
    setAgeFilter('');
    setEngagedFilter(false);
    setNotEngagedFilter(false);
    setCoverageFilter(new Set([]));
    init();
  }

  const integrateCaregiverPageWithAssignedFamilyData = async (
    page: CareGiver[],
    uid: string,
  ): Promise<CaregiverWithAssignedFamilyData[]> => {
    const newFamilyPage: CaregiverWithAssignedFamilyData[] = [];
    for (const family of page) {
      const assignedTo = family.assignedTo;
      let caregiverAssignedFamilyData: CaregiverAssignedFamilyData | null = null;
      if (assignedTo) {
        const caregiverData = await getUserFamily(uid, assignedTo);
        caregiverAssignedFamilyData = { name: caregiverData.personalData.name, surname: caregiverData.personalData.surname };
      }
      newFamilyPage.push({...family, assingedFamilyData: caregiverAssignedFamilyData });
    }
    return newFamilyPage;
  }

  const fetchNextPage = async() => {
    if (!userProfile) return;
    const { page, size, lastDoc, maxPage } = paginationConfig;
    if (!lastDoc) throw 'Invalid Last doc';
    const filters = constructTableFilterObj();
    const nextPagePaginationConfig: TablePaginationConfig = { page: page + 1, size, lastDoc, maxPage };
    const nextPage = await loadCaregiverNextTablePage(userProfile.uid, nextPagePaginationConfig, filters);
    const newCaregiverPage = await integrateCaregiverPageWithAssignedFamilyData(nextPage.data, userProfile.uid);
    setPaginationConfig({page: nextPagePaginationConfig.page, size, firstDoc: nextPage.cursors.first, secondDoc: nextPage.cursors.second, lastDoc: nextPage.cursors.last, maxPage});
    setPage(newCaregiverPage);
  }

  const fetchPrevPage = async () => {
    if (!userProfile) return;
    const { page, size, firstDoc, maxPage } = paginationConfig;
    if (!firstDoc) throw 'Invalid Last doc';
    const filters = constructTableFilterObj();
    const prevPagePaginationConfig: TablePaginationConfig = { page: page - 1, size, firstDoc, maxPage };
    const prevPage = await loadCaregiverPreviousTablePage(userProfile.uid, prevPagePaginationConfig, filters);
    const newCaregiverPage = await integrateCaregiverPageWithAssignedFamilyData(prevPage.data, userProfile.uid);
    setPaginationConfig({page: prevPagePaginationConfig.page, size, firstDoc: prevPage.cursors.first, lastDoc: prevPage.cursors.last, maxPage})
    setPage(newCaregiverPage);
  }

  const constructTableFilterObj = (): CaregiverTableFilter => {
    const coverage_f = Array.from(coverageFilter).length > 0 
      ? Array.from(coverageFilter)[0] as CareGiverCovergaeAvailability
      : null; 
    return {
      name: nameFilter || null,
      surname: surnameFilter || null,
      age: ageFilter || null,
      engaged: engagedFilter,
      notEngaged: notEngaedFilter,
      coverage: coverage_f
    }
  }

  const onCareGiverEdit = async (
    caregiver: CareGiver
  ) => {
    navigate('/edit_caregiver', {state: caregiver});
  }

  const gotoCreateCareGiver = () => {
    navigate('/create_care_giver');
  }

  const onCareGiverDelete = async (
    caregiver: CareGiver
  ) => {
    try {
      if (!userProfile) return;
      await deleteUserCareGiver(userProfile.uid, caregiver);
      discardFilters();
      toast.success('Badante eliminata con successo');
    } catch (error) {
      console.log('err', error);
      toast.error("Erorre durante l'eliminazione della badante");
    }
  }

  const onFamilyRemove = async (
    caregiver: CareGiver
  ) => {
    try {
      if (!userProfile) return;
      const familyId = caregiver.assignedTo;
      if (!familyId) return;
      await removeCaregiverFromFamily(userProfile.uid, familyId, caregiver.id);
      toast.success('Famiglia rimossa con successo');
      discardFilters();
    } catch (error) {
      console.log('err', error);
      toast.error('Si è verificato un errore durante la rimozione della famiglia');
    }
  }

  const onWarningToggle = async (
    id: string,
    value: boolean
  ) => {
    try {
      if (!userProfile) return;
      console.log(id);
      const idx = page.findIndex(el => el.id === id);
      if (idx >= 0) {
        const targetFamily = page[idx];
        await updateCaregiverWarning(userProfile.uid, targetFamily.id, value);
        setPage((prev) => {
          const newPage = brkRef(prev);
          newPage[idx].warning = value;
          return newPage;
        })
      }
    } catch (error) {
      toast.error('Si è verificato un errore');
    }
  }

  return (
    <PageLayout nestedPage>
      <div className='flex justify-between items-center'>
        <div className='flex flex-col'>
          <Typography variant='headline3'>
            Badanti
          </Typography>
          <Typography className='mt-2'>
            Lista badanti create
          </Typography>
        </div>
        <TextButton 
          icon={<AddCircleIcon style={{color: theme.color.primary}}/>}
          onPress={gotoCreateCareGiver}
          title='NUOVA BADANTE'/>
      </div>
      <div className='flex flex-col w-full h-full mt-6 p-4' style={{
        borderRadius: theme.sizing.x2,
        backgroundColor: theme.color.background,
        border: '1.5px solid lightgray'
      }}>
        <Typography variant='button' style={{fontWeight: 600}}>Filtri</Typography>
        <div className='flex flex-row gap-8 items-center mt-2 justify-between'>
          <Input
            label='Nome'
            className='flex-1' 
            value={nameFilter} 
            onValueChange={(val) => {
              setNameFilter(val);
            }}/>
          <Input
            label='Cognome'
            className='flex-1' 
            value={surnameFilter} 
            onValueChange={(val) => {
              setSurnameFilter(val);
            }}/>
          <Input
            label='Età'
            className='flex-1' 
            value={ageFilter} 
            onValueChange={(val) => {
              setAgeFilter(val);
            }}/>
          <Select
            label='Copertura'
            className='flex-1 min-w-[150px]'
            selectionMode='single'
            selectedKeys={coverageFilter}
            onSelectionChange={setCoverageFilter}>
              {
                CAREGIVER_COVERAGE_AVAILABILITY.map(c => (
                  <SelectItem key={c.key}>
                    {c.value}
                  </SelectItem>
                ))
              }
          </Select>
          <Checkbox isSelected={notEngaedFilter} onValueChange={setNotEngagedFilter}>
            Non assegnata
          </Checkbox>
          <Checkbox isSelected={engagedFilter} onValueChange={setEngagedFilter}>
            Assegnata
          </Checkbox>
          <div className='flex flex-row gap-4 itmes-center'>
            <Button title='Applica' variant='primary' onClick={applyTableFilters} />
            <Button title='Cancella' variant='secondary' onClick={discardFilters} />
          </div>
        </div>
        <Divider className='mt-4'/>
        <CareGiversTable 
          className='mt-8' 
          data={page}
          onFamilyRemove={onFamilyRemove}
          onWarningToggle={onWarningToggle}
          onDelete={onCareGiverDelete}
          onEdit={onCareGiverEdit} />
        <div className='flex justify-end gap-4 items-center mt-auto'>
          <NUIButton isIconOnly size='sm' onClick={fetchPrevPage} isDisabled={paginationConfig.page === 1}>
            <ArrowBackIosNewIcon fontSize='small'/>
          </NUIButton>
          <p>{paginationConfig.page}</p>
          <NUIButton isIconOnly size='sm' onClick={fetchNextPage} isDisabled={paginationConfig.page >= totalNumeberOfPages}>
            <ArrowForwardIosIcon fontSize='small'/>
          </NUIButton>
        </div>
      </div>
    </PageLayout>
  )
}